import { Sun, Cloud, CloudRain, CloudLightning, CloudSnow, CloudFog, Moon, Wind, Droplets } from "lucide-react";

// Representative regions → city id (first 6 available are shown)
const GAME_CITIES = [
  { id: "atlanta", region: "Metro Atlanta" },
  { id: "rome", region: "Northwest GA" },
  { id: "athens", region: "Northeast GA" },
  { id: "columbus", region: "West GA" },
  { id: "macon", region: "Middle GA" },
  { id: "savannah", region: "Coastal GA" },
  { id: "valdosta", region: "South GA" },
  { id: "augusta", region: "East GA" },
];

function iconFor(cond = "") {
  const c = cond.toLowerCase();
  if (/thunder|tstorm|storm/.test(c)) return CloudLightning;
  if (/snow|sleet|flurr|wintry|ice/.test(c)) return CloudSnow;
  if (/rain|shower|drizzle/.test(c)) return CloudRain;
  if (/fog|haze|mist/.test(c)) return CloudFog;
  if (/sun|clear/.test(c)) return Moon; // Friday night → clear = moon
  if (/cloud|overcast/.test(c)) return Cloud;
  return Cloud;
}

function fridayNightPeriod(city) {
  const periods = city?.forecast_periods || [];
  let p = periods.find((x) => x.isDaytime === false && /friday/i.test(x.name || ""));
  if (!p && new Date().getDay() === 5) p = periods.find((x) => x.isDaytime === false);
  return p || null;
}

const TONE = { good: "#16a34a", watch: "#f59e0b", bad: "#dc2626" };

function vibe(temp, pop, cond = "") {
  const c = cond.toLowerCase();
  if (/thunder|storm/.test(c)) return { label: "Storm Risk", tone: "bad" };
  if (pop >= 50 || /rain|shower/.test(c)) return { label: "Rain Gear", tone: "watch" };
  if (temp != null && temp <= 42) return { label: "Bundle Up", tone: "watch" };
  if (temp != null && temp >= 48 && temp <= 76) return { label: "Perfect FB Weather", tone: "good" };
  return { label: "Game On", tone: "good" };
}

function nextFridayLabel() {
  const d = new Date();
  const add = (5 - d.getDay() + 7) % 7; // days until Friday
  d.setDate(d.getDate() + add);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

export function buildKickoffGames(cities) {
  const byId = Object.fromEntries((cities || []).map((c) => [c.id, c]));
  const games = [];
  for (const g of GAME_CITIES) {
    const city = byId[g.id];
    if (!city) continue;
    const p = fridayNightPeriod(city);
    if (!p) continue;
    const pop = p.probabilityOfPrecipitation?.value ?? 0;
    games.push({
      region: g.region,
      city: city.name,
      temp: p.temperature,
      cond: p.shortForecast || "—",
      pop,
      wind: p.windSpeed || "—",
      vibe: vibe(p.temperature, pop, p.shortForecast),
    });
    if (games.length >= 6) break;
  }
  return games;
}

function GameCard({ g }) {
  const Icon = iconFor(g.cond);
  const tone = TONE[g.vibe.tone];
  return (
    <div className="flex h-full items-center gap-6 rounded-[8px] bg-white px-8 py-5 shadow-[0_12px_30px_rgba(0,0,0,0.25)]" style={{ borderLeft: `16px solid ${tone}` }} data-testid={`fnl-game-${g.region.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="h-20 w-20 shrink-0" style={{ color: tone }} />
      <div className="min-w-0 flex-1">
        <div className="font-oswald text-[44px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]">{g.region}</div>
        <div className="mt-1 font-oswald text-[26px] font-semibold uppercase tracking-wide text-[#5b6675]">{g.city} · {g.cond}</div>
        <div className="mt-2 flex items-center gap-6 font-oswald text-[27px] font-bold uppercase tracking-wide text-[#2c3542]">
          <span className="flex items-center gap-2"><Droplets className="h-6 w-6 text-[#2a74d8]" />{g.pop}%</span>
          <span className="flex items-center gap-2"><Wind className="h-6 w-6 text-[#2a74d8]" />{g.wind}</span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-oswald text-[92px] font-black leading-none text-[#0a0f1c]">{g.temp ?? "—"}°</div>
        <div className="mt-2 inline-block rounded-[4px] px-3 py-1 font-oswald text-[23px] font-black uppercase tracking-wider text-white" style={{ background: tone }}>{g.vibe.label}</div>
      </div>
    </div>
  );
}

export function FridayNightSlide({ cities }) {
  const games = buildKickoffGames(cities);
  if (!games.length) return null;
  return (
    <div data-testid="fnl-slide" className="relative h-full w-full overflow-hidden" style={{ background: "radial-gradient(1200px 600px at 50% -10%, #16407a 0%, #0a1f45 55%, #060f26 100%)" }}>
      {/* stadium-light glow accents */}
      <div className="pointer-events-none absolute -top-16 left-1/4 h-64 w-64 rounded-full bg-[#ffd60a]/10 blur-3xl" />
      <div className="pointer-events-none absolute -top-16 right-1/4 h-64 w-64 rounded-full bg-[#ffd60a]/10 blur-3xl" />
      <div className="relative flex h-full w-full flex-col px-[52px] pt-[24px] pb-[28px]">
        <div className="mb-5 flex items-end justify-between">
          <div>
            <div className="font-oswald text-[23px] font-bold uppercase tracking-[0.3em] text-[#ffd60a]">Friday Night Lights</div>
            <div className="font-oswald text-[56px] font-black uppercase leading-none tracking-wide text-white">Kickoff Forecast</div>
          </div>
          <div className="text-right font-oswald text-[28px] font-bold uppercase tracking-wide text-white/80">{nextFridayLabel()}<br /><span className="text-[18px] tracking-widest text-white/55">~7:30 PM Kickoff</span></div>
        </div>
        <div className="grid flex-1 grid-cols-2 auto-rows-fr gap-x-6 gap-y-4">
          {games.map((g) => <GameCard key={g.region} g={g} />)}
        </div>
      </div>
    </div>
  );
}
